import React, { useState, useMemo } from 'react';
import { X, Wand2, ChevronRight, ChevronLeft, Save, Loader2, AlertCircle, Check, User } from 'lucide-react';
import { Player, Lineup, LiberoAssignment, RotationalSubstitution } from '../types';
import { supabase, mapLineupFromDB } from '../services/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

type System = '5-1' | '6-2';
type Step = 'system' | 'fix-roles' | 'court';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  teamPlayers: Player[];
  organizationId: string;
  teamId: string;
  setLineups: React.Dispatch<React.SetStateAction<Lineup[]>>;
  allowTwoLiberos?: boolean;
  onAllowTwoLiberosChange?: (val: boolean) => void;
  onSave?: (lineup: Lineup) => void;
}

interface Missing {
  role: string;
  label: string;
  need: number;
  have: number;
  short: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const matchesRole = (pos: string, role: string): boolean => {
  if (role === 'Opposite') return pos === 'Opposite' || pos === 'Right Side';
  return pos === role;
};

const getByRole = (players: Player[], role: string, exclude: string[] = []): Player[] =>
  players
    .filter(p => matchesRole(p.position, role) && !exclude.includes(p.id))
    .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));

const REQS: Record<System, Array<{ role: string; label: string; need: number }>> = {
  '5-1': [
    { role: 'Setter', label: 'Setter', need: 1 },
    { role: 'Outside Hitter', label: 'Outside Hitter', need: 2 },
    { role: 'Opposite', label: 'Opposite', need: 1 },
    { role: 'Middle Blocker', label: 'Middle Blocker', need: 2 },
  ],
  '6-2': [
    { role: 'Setter',        label: 'Setter',         need: 2 },
    { role: 'Outside Hitter',label: 'Outside Hitter', need: 2 },
    { role: 'Opposite',      label: 'Opposite',       need: 2 },
    { role: 'Middle Blocker',label: 'Middle Blocker', need: 2 },
  ],
};

const getMissing = (players: Player[], system: System): Missing[] =>
  REQS[system]
    .map(r => ({ ...r, have: getByRole(players, r.role).length, short: Math.max(0, r.need - getByRole(players, r.role).length) }))
    .filter(r => r.short > 0);

// Build position map from depth chart + role overrides (playerId → tempRole)
const buildPositions = (players: Player[], system: System, overrides: Record<string, string>): Record<string, string> => {
  const effective = players.map(p => ({ ...p, position: overrides[p.id] ?? p.position }));
  const top = (role: string, ex: string[] = []) => getByRole(effective, role, ex)[0]?.id ?? '';

  if (system === '5-1') {
    const s1  = top('Setter');
    const oh1 = top('Outside Hitter');
    const mb1 = top('Middle Blocker');
    const op1 = top('Opposite');
    const oh2 = top('Outside Hitter', [oh1]);
    const mb2 = top('Middle Blocker', [mb1]);
    return { p1: s1, p2: oh1, p3: mb1, p4: op1, p5: oh2, p6: mb2 };
  } else {
    // 6-2: same starting court as 5-1 — S2 and Oppo2 start on bench for rotational subs
    const s1  = top('Setter');
    const oh1 = top('Outside Hitter');
    const mb1 = top('Middle Blocker');
    const op1 = top('Opposite');
    const oh2 = top('Outside Hitter', [oh1]);
    const mb2 = top('Middle Blocker', [mb1]);
    return { p1: s1, p2: oh1, p3: mb1, p4: op1, p5: oh2, p6: mb2 };
  }
};

// Auto-generate the 4 rotational substitutions for a 6-2 lineup.
// Starting court: S1(p1) Oppo1(p4) — S2 and Oppo2 start on bench.
// Cycle: S1→front ⟹ Oppo2 in; Oppo2→back ⟹ S1 in; Oppo1→back ⟹ S2 in; S2→front ⟹ Oppo1 in
const build62Subs = (players: Player[], positions: Record<string, string>): RotationalSubstitution[] => {
  const s1Id    = positions.p1;
  const oppo1Id = positions.p4;
  const s2      = getByRole(players, 'Setter',   [s1Id])[0];
  const oppo2   = getByRole(players, 'Opposite', [oppo1Id])[0];
  if (!s2 || !oppo2) return [];
  const ts = Date.now();
  return [
    { id: `62-${ts}-1`, playerOffId: s1Id,     playerOnId: oppo2.id, triggerType: 'front_row', ignoreFirstTime: false },
    { id: `62-${ts}-2`, playerOffId: oppo2.id, playerOnId: s1Id,     triggerType: 'back_row',  ignoreFirstTime: false },
    { id: `62-${ts}-3`, playerOffId: oppo1Id,  playerOnId: s2.id,    triggerType: 'back_row',  ignoreFirstTime: false },
    { id: `62-${ts}-4`, playerOffId: s2.id,    playerOnId: oppo1Id,  triggerType: 'front_row', ignoreFirstTime: false },
  ];
};

// Court layout: back row at top (p1,p6,p5), front row at bottom (p2,p3,p4), NET below
const BACK_ROW: string[]  = ['p1', 'p6', 'p5'];
const FRONT_ROW: string[] = ['p2', 'p3', 'p4'];

const SLOT_LABEL: Record<string, string> = {
  p1: 'RB', p2: 'RF', p3: 'MF', p4: 'LF', p5: 'LB', p6: 'MB',
};
const SLOT_ROMAN: Record<string, string> = {
  p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI',
};
const SLOT_ROLE_51: Record<string, string> = {
  p1: 'Setter', p2: 'Outside 1', p3: 'Middle 1', p4: 'Opposite', p5: 'Outside 2', p6: 'Middle 2',
};
const SLOT_ROLE_62: Record<string, string> = {
  p1: 'Setter 1', p2: 'Outside 1', p3: 'Middle 1', p4: 'Opposite', p5: 'Outside 2', p6: 'Middle 2',
};

// ── System mini court (system-selection preview) ─────────────────────────────

interface MiniCell { numeral: string; abbrev: string; label: string }

const MINI_COURTS: Record<System, { back: MiniCell[]; front: MiniCell[] }> = {
  '5-1': {
    back:  [
      { numeral: 'I',   abbrev: 'S',    label: 'Setter'    },
      { numeral: 'VI',  abbrev: 'MB',   label: 'Middle 2'  },
      { numeral: 'V',   abbrev: 'OH',   label: 'Outside 2' },
    ],
    front: [
      { numeral: 'II',  abbrev: 'OH',   label: 'Outside 1' },
      { numeral: 'III', abbrev: 'MB',   label: 'Middle 1'  },
      { numeral: 'IV',  abbrev: 'Oppo', label: 'Opposite'  },
    ],
  },
  '6-2': {
    back:  [
      { numeral: 'I',   abbrev: 'S1',   label: 'Setter 1'  },
      { numeral: 'VI',  abbrev: 'MB',   label: 'Middle 2'  },
      { numeral: 'V',   abbrev: 'OH',   label: 'Outside 2' },
    ],
    front: [
      { numeral: 'II',  abbrev: 'OH',   label: 'Outside 1' },
      { numeral: 'III', abbrev: 'MB',   label: 'Middle 1'  },
      { numeral: 'IV',  abbrev: 'Oppo', label: 'Opposite'  },
    ],
  },
};

const SystemMiniCourt: React.FC<{ system: System }> = ({ system }) => {
  const { back, front } = MINI_COURTS[system];
  const cell = (c: MiniCell) => (
    <div key={c.numeral} className="py-1.5 px-1 flex flex-col items-center gap-0.5 bg-white">
      <span className="text-[8px] font-bold text-slate-400">{c.numeral}</span>
      <span className="font-black text-sm leading-none text-slate-700">{c.abbrev}</span>
      <span className="text-slate-400 truncate w-full text-center leading-tight text-[8px]">{c.label}</span>
    </div>
  );
  return (
    <div className="mt-3">
      <div className="border border-slate-200 rounded-t-lg overflow-hidden text-[10px]">
        <div className="grid grid-cols-3 divide-x divide-slate-200">{back.map(cell)}</div>
        <div className="h-px bg-slate-200" />
        <div className="grid grid-cols-3 divide-x divide-slate-200">{front.map(cell)}</div>
      </div>
      <div className="flex items-center gap-1 rounded-b-lg border border-t-0 border-slate-200 bg-slate-200 px-2 py-0.5">
        <div className="flex-1 h-px bg-slate-400" />
        <span className="text-[9px] font-black text-slate-500 tracking-widest uppercase">Net</span>
        <div className="flex-1 h-px bg-slate-400" />
      </div>
    </div>
  );
};

// ── Slot Card (court) ─────────────────────────────────────────────────────────

interface SlotCardProps {
  slot: string; system: System; playerId: string; players: Player[];
  selected: boolean; dragOver: boolean; isLiberoReplaced?: boolean;
  onClick: () => void;
  onDragStart: () => void; onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void; onDragEnd: () => void;
}

const SlotCard: React.FC<SlotCardProps> = ({
  slot, system, playerId, players, selected, dragOver, isLiberoReplaced,
  onClick, onDragStart, onDragOver, onDrop, onDragEnd,
}) => {
  const player = players.find(p => p.id === playerId);
  const roleLabel = system === '5-1' ? SLOT_ROLE_51[slot] : SLOT_ROLE_62[slot];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center rounded-xl border-2 p-2 cursor-pointer select-none transition-all h-[86px]
        ${selected ? 'border-blue-500 bg-blue-50 shadow-md shadow-blue-100 scale-[1.02]' : ''}
        ${dragOver && !selected ? 'border-blue-400 bg-blue-50' : ''}
        ${!selected && !dragOver ? 'border-slate-200 bg-white hover:border-blue-300' : ''}
        ${slot === 'p1' && !selected ? 'border-amber-400 bg-amber-50' : ''}
      `}
    >
      {/* Roman numeral only */}
      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{SLOT_ROMAN[slot]}</span>

      {/* Libero-replaces badge */}
      {isLiberoReplaced && (
        <div className="absolute top-1 left-1 bg-violet-500 text-white text-[8px] font-black px-1 py-0.5 rounded leading-none">L</div>
      )}

      {player ? (
        <>
          {player.photoUrl ? (
            <div className="w-8 h-8 rounded-full overflow-hidden border border-white shadow-sm mb-0.5">
              <img src={player.photoUrl} className="w-full h-full object-cover" alt="" />
            </div>
          ) : (
            <span className="text-lg font-black text-slate-800 leading-none mb-0.5">#{player.jerseyNumber || '?'}</span>
          )}
          <span className="text-[10px] font-semibold text-slate-700 truncate w-full text-center px-1">{player.name.split(' ')[0]}</span>
        </>
      ) : (
        <div className="flex flex-col items-center opacity-30">
          <User className="w-4 h-4" />
          <span className="text-[9px] font-bold mt-0.5">Empty</span>
        </div>
      )}

      <span className="text-[8px] font-semibold text-slate-400 mt-0.5 truncate w-full text-center">{roleLabel}</span>

      {selected && (
        <div className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-full">swap</div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const QuickLineupBuilder: React.FC<Props> = ({
  isOpen, onClose, teamPlayers, organizationId, teamId, setLineups, allowTwoLiberos = false, onAllowTwoLiberosChange, onSave,
}) => {
  const [step, setStep]           = useState<Step>('system');
  const [system, setSystem]       = useState<System>('5-1');
  const [overrides, setOverrides] = useState<Record<string, string>>({});  // playerId → tempRole
  const [positions, setPositions] = useState<Record<string, string>>({});
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [dragSlot, setDragSlot]   = useState<string | null>(null);
  const [dragOver, setDragOver]   = useState<string | null>(null);
  const [lineupName, setLineupName] = useState('');
  const [isSaving, setIsSaving]   = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // liberoId → player IDs they sandwich (replace in back row)
  const [liberoSandwiches, setLiberoSandwiches] = useState<Record<string, string[]>>({});
  // 6-2: per-setter dual-role — each ID in this array plays all 6 rotations (oppo when front row, no sub)
  const [dualRoleSetterIds, setDualRoleSetterIds] = useState<string[]>([]);
  // 1 or 2 liberos — synced to the global allowTwoLiberos setting
  const [liberoCount, setLiberoCount] = useState<1 | 2>(allowTwoLiberos ? 2 : 1);

  const handleLiberoCountChange = (n: 1 | 2) => {
    setLiberoCount(n);
    onAllowTwoLiberosChange?.(n === 2);
  };

  const toggleDualRoleSetter = (id: string) =>
    setDualRoleSetterIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Players with effective positions (base + overrides)
  const effectivePlayers = useMemo(
    () => teamPlayers.map(p => ({ ...p, position: overrides[p.id] ?? p.position })),
    [teamPlayers, overrides]
  );

  const missing = useMemo(() => {
    if (system === '6-2' && dualRoleSetterIds.length > 0) {
      // Each dual-role setter covers one Opposite slot — reduce requirement accordingly
      const oppositeNeed = Math.max(0, 2 - dualRoleSetterIds.length);
      return REQS['6-2']
        .map(r => r.role === 'Opposite' ? { ...r, need: oppositeNeed } : r)
        .map(r => ({ ...r, have: getByRole(effectivePlayers, r.role).length, short: Math.max(0, r.need - getByRole(effectivePlayers, r.role).length) }))
        .filter(r => r.short > 0);
    }
    return getMissing(effectivePlayers, system);
  }, [effectivePlayers, system, dualRoleSetterIds]);

  // Players not yet assigned in the lineup slots (for override dropdowns)
  const unpositionedPlayers = useMemo(
    () => teamPlayers.filter(p => !['Setter','Outside Hitter','Opposite','Right Side','Middle Blocker'].includes(p.position) && !overrides[p.id]),
    [teamPlayers, overrides]
  );

  const reset = () => {
    setStep('system'); setSystem('5-1'); setOverrides({}); setPositions({});
    setSelectedSlot(null); setLineupName(''); setSaveError(null); setLiberoSandwiches({});
    setDualRoleSetterIds([]);
    // Don't reset liberoCount — it mirrors the persistent setting
  };

  const handleClose = () => { reset(); onClose(); };

  // ── Step navigation ──────────────────────────────────────────────────────────

  const goToCourt = () => {
    let pos: Record<string, string>;
    if (system === '6-2' && dualRoleSetterIds.length > 0) {
      // When any setter is dual-role: place S1 at p1 (back row), S2 at p4 (front row plays oppo)
      const top = (role: string, ex: string[] = []) => getByRole(effectivePlayers, role, ex)[0]?.id ?? '';
      const s1  = top('Setter');
      const s2  = getByRole(effectivePlayers, 'Setter', [s1])[0]?.id ?? '';
      const oh1 = top('Outside Hitter');
      const mb1 = top('Middle Blocker');
      const oh2 = top('Outside Hitter', [oh1]);
      const mb2 = top('Middle Blocker', [mb1]);
      pos = { p1: s1, p2: oh1, p3: mb1, p4: s2, p5: oh2, p6: mb2 };
    } else {
      pos = buildPositions(effectivePlayers, system, {});
    }
    setPositions(pos);
    setLineupName(prev => prev || `My ${system}`);
    // Default libero sandwich assignments
    const liberoPlayers = effectivePlayers.filter(p => p.position === 'Libero');
    const sandwiches: Record<string, string[]> = {};
    if (liberoCount === 2 && liberoPlayers.length >= 2) {
      sandwiches[liberoPlayers[0].id] = [pos.p3].filter(Boolean);
      sandwiches[liberoPlayers[1].id] = [pos.p6].filter(Boolean);
    } else if (liberoPlayers.length >= 1) {
      sandwiches[liberoPlayers[0].id] = [pos.p3, pos.p6].filter(Boolean);
    }
    setLiberoSandwiches(sandwiches);
    setStep('court');
  };

  const handleSystemNext = () => {
    if (missing.length > 0) setStep('fix-roles');
    else goToCourt();
  };

  const handleFixNext = () => {
    if (missing.length > 0) return;
    goToCourt();
  };

  // ── Court drag-and-drop / tap-to-swap ────────────────────────────────────────

  const handleSlotClick = (slot: string) => {
    if (!selectedSlot) { setSelectedSlot(slot); return; }
    if (selectedSlot === slot) { setSelectedSlot(null); return; }
    setPositions(prev => ({ ...prev, [slot]: prev[selectedSlot!], [selectedSlot!]: prev[slot] }));
    setSelectedSlot(null);
  };

  const handleDragStart = (slot: string) => { setDragSlot(slot); setSelectedSlot(null); };
  const handleDragOver  = (e: React.DragEvent, slot: string) => { e.preventDefault(); setDragOver(slot); };
  const handleDrop      = (slot: string) => {
    if (dragSlot && dragSlot !== slot) {
      setPositions(prev => ({ ...prev, [slot]: prev[dragSlot], [dragSlot]: prev[slot] }));
    }
    setDragSlot(null); setDragOver(null);
  };
  const handleDragEnd   = () => { setDragSlot(null); setDragOver(null); };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const isValidUuid = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  const handleSave = async () => {
    if (!lineupName.trim()) return;
    if (!isValidUuid(teamId)) { setSaveError('No team selected — please select a team before saving.'); return; }
    setIsSaving(true); setSaveError(null);

    // Build libero assignments from user-configured sandwiches
    const liberoAssignments: LiberoAssignment[] = effectivePlayers
      .filter(p => p.position === 'Libero' && (liberoSandwiches[p.id]?.length ?? 0) > 0)
      .map(p => ({ liberoId: p.id, replacesPlayerIds: liberoSandwiches[p.id] || [] }));

    // If any setter is dual-role, skip rotational subs entirely (they play all the way around)
    const rotationalSubstitutions = system === '6-2' && dualRoleSetterIds.length === 0
      ? build62Subs(effectivePlayers, positions)
      : [];

    if (organizationId === 'dev-org-id') {
      const saved: Lineup = {
        id: `temp-lineup-${Date.now()}`, organizationId, teamId,
        name: lineupName.trim(),
        positions: positions as Lineup['positions'],
        liberoAssignments, rotationalSubstitutions,
        system, dualRoleSetterIds,
        isDefault: false, isFavorite: false,
        createdAt: new Date().toISOString(),
      };
      setLineups(prev => [...prev, saved]);
      onSave?.(saved);
      setIsSaving(false);
      handleClose();
      return;
    }

    const isColError = (e: any) => e?.code === '42703' || (e?.message || '').includes('column');

    const persist = async (withRotSubs: boolean, withSystemCols: boolean) => {
      const payload: Record<string, any> = {
        organization_id: organizationId, team_id: teamId,
        name: lineupName.trim(), positions: positions as Lineup['positions'],
        libero_assignments: liberoAssignments, is_default: false, is_favorite: false,
      };
      if (withRotSubs) payload.rotational_substitutions = rotationalSubstitutions;
      if (withSystemCols) {
        payload.system = system;
        payload.dual_role_setter_ids = dualRoleSetterIds.length > 0 ? dualRoleSetterIds : null;
      }
      const { data, error } = await supabase.from('lineups').insert([payload]).select();
      if (error) throw error;
      const result = data?.[0] ? mapLineupFromDB(data[0]) : null;
      // Preserve in-memory values if DB columns don't exist yet
      if (result && !withSystemCols) { result.system = system; result.dualRoleSetterIds = dualRoleSetterIds; }
      if (result && !withRotSubs) result.rotationalSubstitutions = rotationalSubstitutions;
      return result;
    };

    try {
      let saved: Lineup | null = null;
      try { saved = await persist(true, true); }
      catch (e: any) {
        if (isColError(e)) {
          try { saved = await persist(false, true); }
          catch (e2: any) {
            if (isColError(e2)) { saved = await persist(false, false); }
            else throw e2;
          }
        } else throw e;
      }
      if (saved) { setLineups(prev => [...prev, saved!]); onSave?.(saved); }
      handleClose();
    } catch (e: any) {
      console.error('QuickLineupBuilder save error:', e);
      setSaveError(e?.message || 'Failed to save lineup.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // ── Render ────────────────────────────────────────────────────────────────────

  const stepLabels: Step[] = missing.length > 0 || step === 'fix-roles'
    ? ['system', 'fix-roles', 'court']
    : ['system', 'court'];
  const stepIndex = stepLabels.indexOf(step);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-lg text-slate-900">Quick Lineup Builder</h2>
          </div>
          <button type="button" onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Step progress dots */}
        <div className="flex justify-center gap-2 py-3 shrink-0">
          {stepLabels.map((s, i) => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${i === stepIndex ? 'w-6 bg-blue-500' : i < stepIndex ? 'w-3 bg-blue-300' : 'w-3 bg-slate-200'}`} />
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">

          {/* ── STEP: system selection ── */}
          {step === 'system' && (
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-slate-800 text-base">Choose a lineup system</h3>
                <p className="text-sm text-slate-500 mt-0.5">SideoutIQ will auto-fill your best players based on your depth chart.</p>
              </div>

              <div className="space-y-3">
                {/* 5-1 */}
                <button
                  type="button"
                  onClick={() => { setSystem('5-1'); setDualRoleSetterIds([]); }}
                  className={`w-full text-left rounded-xl border-2 p-4 transition-all ${system === '5-1' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-black text-slate-900 text-lg">5-1</span>
                        {system === '5-1' && <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Default</span>}
                      </div>
                      <p className="font-semibold text-slate-700 text-sm mt-0.5">1 Setter (Plays all 6 rotations)</p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                        One setter runs the offense from any position on the court. Ideal for teams with a strong, experienced setter. The most common system at all levels.
                      </p>
                    </div>
                    <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ml-3 ${system === '5-1' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                      {system === '5-1' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <SystemMiniCourt system="5-1" />
                  <div className="mt-1.5 text-[10px] text-slate-500">
                    <span className="font-bold">Needs:</span> 1 Setter · 2 Outside Hitters · 1 Opposite · 2 Middles
                  </div>
                </button>

                {/* 6-2 */}
                <button
                  type="button"
                  onClick={() => setSystem('6-2')}
                  className={`w-full text-left rounded-xl border-2 p-4 transition-all ${system === '6-2' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-black text-slate-900 text-lg">6-2</span>
                      </div>
                      <p className="font-semibold text-slate-700 text-sm mt-0.5">2 Setters (Always have 3 attackers in the front row)</p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                        Two setters alternate — whichever is in the back row runs the offense. When a setter rotates to the front row, they attack like any other player. Great for teams with two good setters.
                      </p>
                      {system === '6-2' && dualRoleSetterIds.length === 0 && (
                        <p className="text-xs text-green-700 bg-green-50 rounded-lg px-2 py-1 mt-2 font-medium">
                          ✓ All 4 setter-swap rotational substitutions will be pre-configured automatically.
                        </p>
                      )}
                    </div>
                    <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ml-3 ${system === '6-2' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                      {system === '6-2' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <SystemMiniCourt system="6-2" />
                  <div className="mt-1.5 text-[10px] text-slate-500">
                    {dualRoleSetterIds.length > 0
                      ? <><span className="font-bold">Needs:</span> 2 Setters · 2 Outside Hitters · {Math.max(0, 2 - dualRoleSetterIds.length) > 0 ? `${Math.max(0, 2 - dualRoleSetterIds.length)} Opposite · ` : ''}2 Middles</>
                      : <><span className="font-bold">Needs:</span> 2 Setters · 2 Outside Hitters · 2 Opposites · 2 Middles</>
                    }
                  </div>
                </button>

                {/* Per-setter dual-role toggles — shown when 6-2 is selected */}
                {system === '6-2' && (() => {
                  const setterPlayers = getByRole(effectivePlayers, 'Setter');
                  if (setterPlayers.length === 0) return null;
                  return (
                    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                      <div>
                        <p className="text-sm font-bold text-slate-800">Which setters play all the way around?</p>
                        <p className="text-xs text-slate-500 mt-0.5">Checked setters stay on court in all 6 rotations — setting from back row, playing opposite from front row. No sub needed for them.</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {setterPlayers.map(p => {
                          const isChecked = dualRoleSetterIds.includes(p.id);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => toggleDualRoleSetter(p.id)}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${isChecked ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isChecked ? 'border-amber-500 bg-amber-500' : 'border-slate-300 bg-white'}`}>
                                {isChecked && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="min-w-0">
                                <span className="text-sm font-bold text-slate-800">#{p.jerseyNumber || '?'} {p.name}</span>
                                {isChecked && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Plays all the way around</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {dualRoleSetterIds.length > 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 font-medium">
                          ✓ Checked setters will show as <strong>Oppo</strong> when front row in GameTime. No rotational subs will be generated.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Libero count */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-bold text-slate-800 mb-0.5">Liberos</p>
                <p className="text-xs text-slate-500 mb-3">How many liberos does your team designate per set?</p>
                <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                  {([1, 2] as const).map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => handleLiberoCountChange(n)}
                      className={`flex-1 px-4 py-2 text-sm font-bold transition-colors ${liberoCount === n ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      {n} Libero{n === 2 ? 's' : ''}
                    </button>
                  ))}
                </div>
                {liberoCount === 2 && (
                  <p className="text-[11px] text-purple-700 mt-2">Each libero will be assigned to cover one Middle Blocker.</p>
                )}
              </div>

              <button
                type="button"
                onClick={handleSystemNext}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors shadow-sm shadow-blue-600/20"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ── STEP: fix missing roles ── */}
          {step === 'fix-roles' && (
            <div className="space-y-5">
              <div>
                <h3 className="font-bold text-slate-800 text-base">Assign missing positions</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Your team is missing some required positions for a {system}. Quickly assign roles below to continue.
                </p>
              </div>

              {missing.map(m => {
                const currentCount = getByRole(effectivePlayers, m.role).length;
                const stillNeed = Math.max(0, m.need - currentCount);
                if (stillNeed === 0) return null;
                // Players without the needed role and not yet overridden to it
                const available = teamPlayers.filter(p => {
                  const eff = overrides[p.id] ?? p.position;
                  return !matchesRole(eff, m.role);
                });
                return (
                  <div key={m.role} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                      <span className="font-bold text-amber-800 text-sm">
                        Need {stillNeed} more {m.label}{stillNeed > 1 ? '' : ''}
                      </span>
                    </div>
                    <p className="text-xs text-amber-700 mb-3">
                      Select {stillNeed === 1 ? 'a player' : `${stillNeed} players`} to play {m.label}:
                    </p>
                    <div className="space-y-2">
                      {available.map(p => {
                        const currentRole = overrides[p.id] ?? p.position;
                        const isAssigned = matchesRole(overrides[p.id] ?? '', m.role);
                        return (
                          <label key={p.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-all ${isAssigned ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-blue-600"
                              checked={isAssigned}
                              onChange={e => {
                                setOverrides(prev => {
                                  const next = { ...prev };
                                  if (e.target.checked) {
                                    next[p.id] = m.role;
                                  } else {
                                    delete next[p.id];
                                  }
                                  return next;
                                });
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-sm text-slate-800">#{p.jerseyNumber} {p.name}</span>
                              <span className="ml-2 text-xs text-slate-500">({currentRole})</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Resolved roles */}
              {REQS[system].filter(r => getMissing(effectivePlayers, system).every(m => m.role !== r.role) && r.need > 0).map(r => (
                <div key={r.role} className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-4 py-2.5">
                  <Check className="w-4 h-4" />
                  <span className="font-semibold">{r.label}</span>
                  <span className="text-emerald-600">— {r.need} assigned ✓</span>
                </div>
              ))}

              <p className="text-xs text-slate-400 text-center">
                These temporary role assignments only apply to this lineup. Update player positions in your Roster to make them permanent.
              </p>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('system')} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="button"
                  onClick={handleFixNext}
                  disabled={missing.length > 0}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-xl transition-colors"
                >
                  Build Lineup <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: court preview + save ── */}
          {step === 'court' && (() => {
            const liberoPlayer = effectivePlayers.find(p => p.position === 'Libero');
            const occupiedIds  = new Set(Object.values(positions).filter(Boolean));
            // Liberos are not court players — exclude them from the bench pool
            const benchPlayers = teamPlayers.filter(p => !occupiedIds.has(p.id) && p.position !== 'Libero');
            // Highlight slots whose occupants are sandwiched by any libero
            const allSandwichedIds = new Set(Object.values(liberoSandwiches).flat());
            const liberoReplacedSlots = new Set(
              Object.entries(positions).filter(([, pid]) => allSandwichedIds.has(pid)).map(([slot]) => slot)
            );

            const handleBenchClick = (playerId: string) => {
              if (!selectedSlot) return;
              // Liberos are not placed into court slots — they enter via sandwich
              const player = teamPlayers.find(p => p.id === playerId);
              if (player?.position === 'Libero') return;
              const evicted = positions[selectedSlot] || '';
              setPositions(prev => ({ ...prev, [selectedSlot]: playerId }));
              // evicted player returns to bench automatically (no longer in positions)
              setSelectedSlot(null);
            };

            return (
            <div className="space-y-5">
              <div>
                <h3 className="font-bold text-slate-800 text-base">Review &amp; adjust your lineup</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Tap a court slot, then tap another slot or a bench player to swap.
                  {system === '5-1' && ' Setter starts in Position I (serving).'}
                </p>
              </div>

              {/* Court */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5">
                {/* Back row */}
                <div className="grid grid-cols-3 gap-1.5">
                  {BACK_ROW.map(slot => (
                    <SlotCard
                      key={slot} slot={slot} system={system}
                      playerId={positions[slot] || ''} players={teamPlayers}
                      selected={selectedSlot === slot} dragOver={dragOver === slot}
                      isLiberoReplaced={!!liberoPlayer && liberoReplacedSlots.has(slot)}
                      onClick={() => handleSlotClick(slot)}
                      onDragStart={() => handleDragStart(slot)}
                      onDragOver={e => handleDragOver(e, slot)}
                      onDrop={() => handleDrop(slot)}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </div>

                {/* Front row */}
                <div className="grid grid-cols-3 gap-1.5">
                  {FRONT_ROW.map(slot => (
                    <SlotCard
                      key={slot} slot={slot} system={system}
                      playerId={positions[slot] || ''} players={teamPlayers}
                      selected={selectedSlot === slot} dragOver={dragOver === slot}
                      isLiberoReplaced={!!liberoPlayer && liberoReplacedSlots.has(slot)}
                      onClick={() => handleSlotClick(slot)}
                      onDragStart={() => handleDragStart(slot)}
                      onDragOver={e => handleDragOver(e, slot)}
                      onDrop={() => handleDrop(slot)}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </div>

                {/* NET */}
                <div className="rounded-lg bg-slate-200 text-center py-1">
                  <span className="text-[10px] font-black text-slate-500 tracking-widest uppercase">NET</span>
                </div>

                {selectedSlot && (
                  <p className="text-center text-xs text-blue-600 font-semibold animate-pulse">
                    Tap another player on court or from the bench to swap
                  </p>
                )}
              </div>

              {/* Bench */}
              {(() => {
                const nonLibero = benchPlayers.filter(p => p.position !== 'Libero');
                if (!nonLibero.length) return null;
                const isTarget = !!selectedSlot;
                return (
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Bench</p>
                    <div className="grid grid-cols-3 gap-2">
                      {nonLibero.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleBenchClick(p.id)}
                          className={`
                            flex flex-col items-center justify-center rounded-xl border-2 p-2 h-[76px] transition-all
                            border-slate-200 bg-white text-slate-800
                            ${isTarget ? 'cursor-pointer hover:border-blue-400 hover:bg-blue-50 active:scale-95' : 'cursor-default opacity-70'}
                          `}
                        >
                          {p.photoUrl ? (
                            <div className="w-7 h-7 rounded-full overflow-hidden border border-white shadow-sm mb-0.5">
                              <img src={p.photoUrl} className="w-full h-full object-cover" alt="" />
                            </div>
                          ) : (
                            <span className="text-base font-black leading-none mb-0.5 text-slate-800">
                              #{p.jerseyNumber || '?'}
                            </span>
                          )}
                          <span className="text-[10px] font-semibold truncate w-full text-center px-1">{p.name.split(' ')[0]}</span>
                          <span className="text-[8px] font-bold mt-0.5 text-slate-400">{p.position.split(' ')[0]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Libero Assignment — shown for up to liberoCount libero players */}
              {(() => {
                const allLiberoPlayers = teamPlayers.filter(p => p.position === 'Libero');
                if (!allLiberoPlayers.length) return null;
                const activeLiberos = allLiberoPlayers.slice(0, liberoCount);
                const lineupPlayerIds = new Set(Object.values(positions).filter(Boolean));
                const lineupPlayers = teamPlayers.filter(p => lineupPlayerIds.has(p.id) && p.position !== 'Libero');
                const isTwoLibMode = liberoCount === 2 && activeLiberos.length > 1;
                const showSlotLabel = isTwoLibMode;
                const posToRoman: Record<string, string> = { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' };
                const playerRotation = (pid: string) => {
                  const slot = Object.entries(positions).find(([, id]) => id === pid)?.[0];
                  return slot ? posToRoman[slot] || '' : '';
                };
                return (
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Libero Assignment</p>
                    <div className="space-y-2">
                      {activeLiberos.map((libero, idx) => {
                        const sandwich = liberoSandwiches[libero.id] || [];
                        return (
                          <div key={libero.id} className="rounded-xl border border-violet-200 bg-violet-50 p-3">
                            <p className="text-xs font-bold text-violet-700 mb-2">
                              {showSlotLabel ? `L${idx + 1}: ` : ''}<span className="text-slate-700">#{libero.jerseyNumber || '?'} {libero.name.split(' ')[0]}</span>
                              <span className="font-normal text-slate-400 ml-1">— plays back row for:</span>
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {lineupPlayers.map(lp => {
                                const isSelected = sandwich.includes(lp.id);
                                const rot = playerRotation(lp.id);
                                return (
                                  <button
                                    key={lp.id}
                                    type="button"
                                    onClick={() => setLiberoSandwiches(prev => {
                                      if (isTwoLibMode) {
                                        return { ...prev, [libero.id]: isSelected ? [] : [lp.id] };
                                      }
                                      const curr = prev[libero.id] || [];
                                      return { ...prev, [libero.id]: isSelected ? curr.filter(id => id !== lp.id) : [...curr, lp.id] };
                                    })}
                                    className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition-all active:scale-95 ${
                                      isSelected ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400 hover:text-violet-700'
                                    }`}
                                  >
                                    #{lp.jerseyNumber || '?'} {lp.name.split(' ')[0]}{rot ? ` (${rot})` : ''}
                                  </button>
                                );
                              })}
                              {lineupPlayers.length === 0 && (
                                <span className="text-[11px] text-slate-400 italic">No court players yet</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Dual-role badge (6-2 with any dual-role setter) */}
              {system === '6-2' && dualRoleSetterIds.length > 0 && (() => {
                const names = dualRoleSetterIds
                  .map(id => { const p = teamPlayers.find(x => x.id === id); return p ? `#${p.jerseyNumber || '?'} ${p.name.split(' ')[0]}` : null; })
                  .filter(Boolean).join(', ');
                return (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                    <p className="text-xs font-bold text-amber-700">6-2 · Dual-Role: {names}</p>
                    <p className="text-[11px] text-amber-600 mt-0.5">These setters play all the way around — no rotational subs needed. They'll appear as Oppo when front row in GameTime.</p>
                  </div>
                );
              })()}

              {/* Rotational subs preview (6-2 traditional only — no dual-role setters) */}
              {system === '6-2' && dualRoleSetterIds.length === 0 && (() => {
                const subs = build62Subs(effectivePlayers, positions);
                if (!subs.length) return null;
                const name = (id: string) => {
                  const p = teamPlayers.find(x => x.id === id);
                  return p ? `#${p.jerseyNumber || '?'} ${p.name.split(' ')[0]}` : '—';
                };
                return (
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Auto-Configured Subs</p>
                    <div className="rounded-xl border border-blue-100 bg-blue-50 divide-y divide-blue-100 overflow-hidden">
                      {subs.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-2 px-3 py-2">
                          <span className={`shrink-0 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full ${s.triggerType === 'front_row' ? 'bg-orange-100 text-orange-600' : 'bg-indigo-100 text-indigo-600'}`}>
                            {s.triggerType === 'front_row' ? '↑ Front' : '↓ Back'}
                          </span>
                          <span className="text-xs font-semibold text-slate-700 truncate">{name(s.playerOffId)}</span>
                          <span className="text-[10px] text-slate-400 shrink-0">out →</span>
                          <span className="text-xs font-semibold text-slate-700 truncate">{name(s.playerOnId)}</span>
                          <span className="text-[9px] text-slate-400 shrink-0">in</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Lineup name */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Lineup name</label>
                <input
                  type="text"
                  value={lineupName}
                  onChange={e => setLineupName(e.target.value)}
                  placeholder="e.g. Main 5-1, Tournament Lineup…"
                  className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  maxLength={60}
                />
              </div>

              {saveError && (
                <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {saveError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(stepLabels[stepIndex - 1] || 'system')}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!lineupName.trim() || isSaving}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-xl transition-colors shadow-sm shadow-blue-600/20"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isSaving ? 'Saving…' : 'Save Lineup'}
                </button>
              </div>
            </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
};

export default QuickLineupBuilder;
